import { useRef, useState, useEffect, Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Menu, MenuItem } from "@progress/kendo-react-layout";
import { Popup } from "@progress/kendo-react-popup";
import { Loader } from "@progress/kendo-react-indicators";
import { SvgIcon } from "@progress/kendo-react-common";
import { cancelOutlineIcon as canceledIcon, exclamationCircleIcon as errorIcon, } from "@progress/kendo-svg-icons";
import { getWidgetKeyFromType, getWidgetFontColors, widgetsInfo, WIDGET_GAUGE_CARD, WIDGET_FUNNEL_CHART, WIDGET_WATERFALL_CHART} from "./utils";

export const ReportingWidget = props =>
{
    const {
        id, widgetTypeId, dataSourceValueType,        
        mode, 
        loaded, loading, errorText, canceled,
        backgroundColor, title, titleFontSize, titleFontIsBold, titleFontIsUnderline, titleFontIsItalic, titleFontColor,
        getContextMenu, onMenuItemClick,
        onWidgetSelect, onWidgetUnSelect, onDoubleClick,
        ...other
    } = props;

    const dispatch = useDispatch();
    const currentTheme = useSelector(state => state.currentTheme);
    const appearance = useSelector(state => state.appearance);
    const key = getWidgetKeyFromType(widgetTypeId);
    const ref = useRef(null);

    //resolving title color
    const fontColors = getWidgetFontColors(currentTheme);
    const titleFontColorResolved = fontColors.find(e => e.id == titleFontColor).color;
    const backgroundColorResolved = backgroundColor ? fontColors.find(e => e.id == backgroundColor).color : undefined;

    /*context menu*/
    const [contextMenuVisible, setContextMenuVisible] = useState(false);

    const contextMenuOffSet = useRef({
        left: 0,
        top: 0,
    });
    
    const handleOnContextMenu = (e) => {  
        //add selected class and fire onWidgetSelect if we right click the widget PROVIDED that it is loaded and has no errors
        if (!ref.current)
            return;

        e.preventDefault();

        //add the selected class if not there and fire onWidgetSelect if exists
        addSelectedClass();

        contextMenuOffSet.current = {
            left: e.clientX,
            top: e.clientY,
        };

        setContextMenuVisible(true);
    };

    const closeContextMenuHandler = () => {
        if (contextMenuVisible)
            setContextMenuVisible(false);
    };
    
    useEffect(() => {
        //when you click anywhere in the document, close the context menu
        document.addEventListener("click", closeContextMenuHandler, true);

        return () => {
            document.removeEventListener("click", closeContextMenuHandler, true);
        };
      }, [contextMenuVisible]);
    /*context menu*/


    /*select unselect*/
    const addSelectedClass = () => {
        if (!ref.current)
            return;

        if (!ref.current.classList.contains("selected"))
        {
            //add the class
            ref.current.classList.add("selected");
    
            //fire onWidgetSelect
            if (onWidgetSelect)
                onWidgetSelect(props, dispatch);               
        }
    };

    const removeSelectedClass = () => {
        if (!ref.current)
            return;

        if (ref.current.classList.contains("selected"))
        {
            //remove the class
            ref.current.classList.remove("selected");
    
            //fire onWidgetUnSelect
            if (onWidgetUnSelect)
                onWidgetUnSelect(props, dispatch);               
        }
    };

    useEffect(() =>{
        const handleSelection = e => {
            //when you click inside the widget, add the selected class and fire onWidgetSelect; reverse when you click outside the widget
            if(ref.current && !ref.current.contains(e.target) && ref.current.classList.contains("selected"))                
                removeSelectedClass();
            else if (ref.current && ref.current.contains(e.target) && !ref.current.classList.contains("selected"))            
                addSelectedClass();            
        };
            
        document.addEventListener('click', handleSelection, true);        
        
        return () => {
            document.removeEventListener('click', handleSelection, true);
        };
    }, []);
    /*select unselect*/


    const handleMenuItemClick = e => {
        const item = e.item;
        const id = item.data;
        const title = item.text;

        if (!title)//it's a separator
           return;
        
        if (onMenuItemClick)
            onMenuItemClick({...item, id: id, title: title, props, dispatch});
    };


    const handleOnDoubleClick = e => {
        if (onDoubleClick)
            onDoubleClick(id, props, dispatch);
    };

    const drawColor = title ? titleFontColorResolved : currentTheme.separatorColor;
    const widgetInfo = widgetsInfo.find(e => e.id == widgetTypeId);
    const UnInitIcon = widgetInfo.icon;
    const unInitIconType = (widgetInfo.id == WIDGET_GAUGE_CARD || widgetInfo.id == WIDGET_FUNNEL_CHART || widgetInfo.id == WIDGET_WATERFALL_CHART) ? 2 : 1;
    const unInitTitle = widgetInfo.title;
   
    return (
        <Fragment>
            <div ref={ref} className={`rpt-widget rpt-widget-${id} rpt-widget-${key}`} onContextMenu={handleOnContextMenu} onDoubleClick={handleOnDoubleClick}
                 style={{backgroundColor: backgroundColorResolved}}
            >                
                {
                    title &&
                    <div className="header" 
                         style={{
                            fontSize: titleFontSize, 
                            color: titleFontColorResolved, 
                            textDecoration: titleFontIsUnderline ? "underline" : "none", 
                            fontStyle: titleFontIsItalic ? "italic" : "normal", 
                            fontWeight: titleFontIsBold ? 700 : 500
                        }}>
                        {title}
                    </div>
                }
                {
                    loaded &&
                    <Fragment>
                        <div className="content" style={{}}>
                            {props.children}
                        </div>                        
                    </Fragment>
                }
                {
                    !loaded && errorText &&
                    <div className="error">
                        <Tooltip anchorElement="target" position="left">
                            <SvgIcon style={{fontSize: 32}} icon={errorIcon} title={errorText} color={currentTheme.error} />
                        </Tooltip>
                    </div>
                }
                {
                    !loaded && canceled &&
                    <div className="canceled">
                        <Tooltip anchorElement="target" position="left">
                            <SvgIcon style={{fontSize: 32}} icon={canceledIcon} title={"Request Canceled!"} color={drawColor} />
                        </Tooltip>
                    </div>
                }
                {
                    !loaded && loading &&
                    <div className="loading">
                        <Loader size={"medium"} type="infinite-spinner" themeColor={"primary"} />
                    </div>
                }
                {
                    !loaded && !errorText && !canceled && !loading && 
                    <div className="uninit" style={{                        
                    }}>
                    {
                        unInitIconType == 1 &&
                        <SvgIcon style={{
                            fontSize: widgetInfo.unInitIconSize.fontSize,
                            color: currentTheme.componentBorderColor,
                            }} icon={UnInitIcon} title={unInitTitle} />
                    }
                    {
                        // if gauge, funnel or waterfall
                        unInitIconType == 2 &&
                        <UnInitIcon className="external-gauge-icon" style={{height:100, width: 100, color: currentTheme.error }} 
                                title={unInitTitle} />
                    }
                    </div>
                }
                <Popup show={contextMenuVisible} offset={contextMenuOffSet.current}>
                    <Menu onSelect={handleMenuItemClick}
                        vertical={true}
                        style={{
                            display: "inline-block",
                        }}
                        className="rpt-widget-context-menu"
                    >
                        {
                            getContextMenu && getContextMenu(props).map((item, index) =>
                            {
                                if (item.title)
                                    return (
                                        <MenuItem key={item.id} data={item.id} text={item.title} icon={item.icon} cssClass="rpt-widget-context-menu-item">
                                            {
                                                item.items && 
                                                item.items.map((e, index) => 
                                                {
                                                    return <MenuItem key={e.id} id={e.id} data={e.id} text={e.title} icon={e.icon} cssClass="rpt-widget-context-menu-item"/>;
                                                })
                                            }
                                        </MenuItem>)
                                else
                                    return <MenuItem key={item.id} cssClass="menu-separator"></MenuItem>
                            })
                        }
                    </Menu>
                </Popup>
            </div>
            
            <style>
                {
                    `
                        .rpt-widget
                        {
                            width: 100%;
                            height: 100%;
                            display: flex;
                            flex-direction: column;
                            align-items: stretch;
                            padding: 8px;
                            border: 1px solid ${currentTheme.separatorColor};
                            border-style: ${mode == 2 && !loaded ? "dashed" : "solid"};
                            border-radius: 6px;
                            background-color: ${currentTheme.componentBg};
                        }

                        .rpt-widget.rpt-widget-ticker
                        {
                            padding:0 4px;
                        }

                        .rpt-widget.selected
                        {
                            border: 2px dotted ${currentTheme.separatorColor};
                        }

                        .rpt-widget > .header
                        {
                            text-align: center;
                            margin-bottom: 8px;
                        }

                        .rpt-widget > .content
                        {
                            display: flex;
                            flex-direction: column;
                            align-items: stretch;
                            flex: 1;
                            overflow: auto;
                        }

                        /*error*/
                        .rpt-widget > .error
                        {
                            color: ${currentTheme.error};
                            font-size: 36px;
                            display: flex;
                            flex: 1;
                            justify-content: center;
                            align-items: center;
                        }

                        .rpt-widget > .error > div
                        {
                            display: flex;
                            justify-content: center;
                            align-items: center;
                        }

                        .rpt-widget > .error .k-icon
                        {
                            font-size:30px;
                        }

                        /*canceled*/
                        .rpt-widget > .canceled
                        {
                            color: ${currentTheme.separatorColor};
                            font-size: 36px;
                            display: flex;
                            flex: 1;
                            justify-content: center;
                            align-items: center;
                        }

                        .rpt-widget > .canceled > div
                        {
                            display: flex;
                            justify-content: center;
                            align-items: center;
                        }

                        .rpt-widget > .canceled .k-icon
                        {
                            font-size:30px;
                        }

                        /*loading*/
                        .rpt-widget > .loading
                        {
                            display: flex;
                            flex: 1;
                            justify-content: center;
                            align-items: center;
                        }

                        /*uninit*/
                        .rpt-widget > .uninit
                        {
                            display: flex;
                            flex: 1;
                            justify-content: center;
                            align-items: center;
                        }

                        .rpt-widget > .uninit svg path
                        {
                            fill: ${currentTheme.componentBorderColor};
                            stroke-width: 2;
                        }

                        /*context menu*/
                        .k-popup .rpt-widget-context-menu
                        {
                            padding:0px;
                            direction: ${appearance.isRightToLeft ? "rtl" : "ltr"}
                        }

                       /* .k-popup .rpt-widget-context-menu-item
                        {
                            padding:4px 18px 4px 12px;
                        }

                        .k-popup .rpt-widget-context-menu-item:hover
                        {
                            background-color: red !important;/* ${currentTheme.hoveredBg};*/
                        }*/

                        .k-popup .rpt-widget-context-menu-separator
                        {
                            height: 1px;
                            width: 100%;
                            border-top: 1px solid ${currentTheme.separatorColor};
                        }

                        .k-popup .rpt-widget-context-menu-item .k-icon
                        {
                            color: ${currentTheme.primary};
                            margin-right: 2px;
                        }

                        .k-popup .rpt-widget-context-menu-item .k-menu-link-text
                        {
                            color: ${currentTheme.componentText};
                        }
                    `
                }
            </style>
            </Fragment>
    );
};